// ============================================================================
// Bulk Delhivery booking modal — launched from the OMS PO list after ticking several eligible rows.
// On open it runs the synchronous bulk-quote for all selected POs and lists them (scrollable): each
// bookable PO gets a per-PO vehicle dropdown (default = auto-pick priority-1); non-bookable POs show
// their reason and are excluded. "Bulk Book" fires the async bulk-book (202) and closes.
// Per-PO results land later in the Delhivery Bookings dashboard / order-status endpoints.
// ============================================================================
import { CloseOutlined } from "@ant-design/icons";
import { Alert, Button, Input, Modal, Segmented, Select, Space, Spin, Table, Tag, Tooltip, Typography, message } from "antd";
import dayjs from "dayjs";
import React from "react";
import {
  DelhiveryBulkQuoteRow,
  delhiveryBulkBook,
  delhiveryBulkQuote,
} from "../../../apis/po.api";
import ContactSelect, { ContactOption, defaultContactValue } from "./contact_select";
import { cargoRejectionReason } from "./delhivery_booking_modal";

const { Text } = Typography;

const rupee = (v: unknown) =>
  v === null || v === undefined || v === "" ? "—" : `₹${v}`;

// Committed customer-delivery date shown on dispatch-due-sourced cards.
const fmtDate = (v?: string | null) => (v ? dayjs(v).format("DD MMM YYYY") : "—");

// One-line summary of a row's open escalation, for the pill's hover.
const escalationTooltip = (e: NonNullable<DelhiveryBulkQuoteRow["escalation"]>): string =>
  [
    e.issue_type,
    e.category,
    e.remarks,
    e.raised_by ? `by ${e.raised_by}` : null,
    e.raised_at ? dayjs(e.raised_at).format("DD MMM") : null,
  ]
    .filter(Boolean)
    .join(" · ") || "Open escalation";

// Union of contact options across bookings, deduped by phone number — feeds the "apply to all"
// pickers so every contact offered on any booking is selectable there too.
const dedupeContactOptions = (opts: ContactOption[]): ContactOption[] => {
  const seen = new Set<string>();
  return opts.filter((o) => (seen.has(o.value) ? false : (seen.add(o.value), true)));
};

// A special pickup "apply to all" option: set each booking's pickup to its OWN vendor contact
// (vendors differ per booking), rather than one shared number.
const VENDOR_CONTACT = "__vendor_contact__";
const PICKUP_EXTRA_OPTIONS = [{ label: "Vendor contact (each booking's own)", value: VENDOR_CONTACT }];

// Per-PO item breakdown, compact. Present on every bulk-quote row (best-effort even for failed POs).
// Item key is allocation_item_id (OMS) or po_item_id (legacy) — fall back to the index.
const ItemsMini: React.FC<{ items?: any[] }> = ({ items }) => {
  if (!Array.isArray(items) || items.length === 0) return null;
  return (
    <Table
      style={{ marginTop: 8 }}
      size="small"
      pagination={false}
      rowKey={(it: any, i) =>
        String(it.allocation_item_id ?? it.po_item_id ?? i)
      }
      dataSource={items}
      scroll={{ x: "max-content" }}
      rowClassName={(it: any) => (it.included ? "" : "delhivery-item-excluded")}
      columns={[
        {
          title: "Item",
          dataIndex: "name",
          render: (name: string, it: any) => (
            <span style={{ color: it.included ? undefined : "#999" }}>
              {name}
              {!it.included && (
                <Tooltip title={it.reason || undefined}>
                  <Tag style={{ marginInlineStart: 6 }}>not shipped</Tag>
                </Tooltip>
              )}
            </span>
          ),
        },
        { title: "Qty", dataIndex: "quantity", width: 56 },
        { title: "Category", dataIndex: "category", render: (c) => c || "—" },
        {
          title: "Weight",
          dataIndex: "weight_kg",
          width: 90,
          render: (w: number | null | undefined, it: any) =>
            !it.included ? "—" : w == null ? (
              <Text type="warning">no density</Text>
            ) : (
              `${w} kg`
            ),
        },
        {
          title: "Max length",
          dataIndex: "max_length_ft",
          width: 100,
          render: (l: number | null | undefined, it: any) =>
            !it.included || l == null ? "—" : `${l} ft`,
        },
      ]}
    />
  );
};

// One booking = one Delhivery order. A consolidated group collapses several quote rows into one.
interface BookingCard {
  key: string; // rep.po_number — the state key and the single PO we book for this card
  rep: DelhiveryBulkQuoteRow; // shared vehicle/fare/contacts/options
  rows: DelhiveryBulkQuoteRow[]; // all rows in the card (combined items)
  pos: string[]; // every PO this booking covers (for the label)
  consolidated: boolean;
}

const DelhiveryBulkModal: React.FC<{
  poNumbers: string[];
  open: boolean;
  onClose: () => void;
  onBooked?: () => void; // called after a successful bulk-book (e.g. to clear the selection)
  // Already-quoted rows (DelhiveryBulkQuoteRow shape) to use as-is instead of running the bulk-quote
  // — e.g. the dispatch-due worklist already quoted these. Skips a slow second quote.
  prequoted?: DelhiveryBulkQuoteRow[];
  // Optional chip shown at the top of the modal (e.g. the active dispatch-due delivery window).
  note?: React.ReactNode;
  // Dispatch-due only: an All/Express/Regular mode toggle shown in the modal header. Present only
  // when the caller passes onModeChange — changing it re-fetches the list (parent-driven). `busy`
  // reflects that re-fetch so the modal shows a loading state during it.
  mode?: "all" | "express" | "regular";
  onModeChange?: (mode: "all" | "express" | "regular") => void;
  busy?: boolean;
}> = ({ poNumbers, open, onClose, onBooked, prequoted, note, mode, onModeChange, busy }) => {
  const [loading, setLoading] = React.useState(false);
  const [results, setResults] = React.useState<DelhiveryBulkQuoteRow[]>([]);
  const [booking, setBooking] = React.useState(false);
  // Per-PO vehicle override the user picked; POs absent here let the backend auto-pick.
  const [vehicleModes, setVehicleModes] = React.useState<Record<string, string>>(
    {}
  );
  // Per-PO shipper (pickup) / receiver (drop) phone overrides — each keyed by po_number.
  const [pickupContacts, setPickupContacts] = React.useState<Record<string, string>>({});
  const [dropContacts, setDropContacts] = React.useState<Record<string, string>>({});
  // "Apply to all" selections — drop is one shared number; pickup can be a shared number too, or the
  // special "Vendor contact" option that fills each booking with its own vendor contact.
  const [bulkPickup, setBulkPickup] = React.useState("");
  const [bulkDrop, setBulkDrop] = React.useState("");
  // Bookings the user crossed out of this batch (keyed by rep.po_number). They disappear from
  // the list and are NOT sent to bulk-book; reopening the modal starts fresh.
  const [removed, setRemoved] = React.useState<Set<string>>(new Set());
  // PO search — filters only what's DISPLAYED (not what gets booked), to find a PO in a long list.
  const [search, setSearch] = React.useState("");

  React.useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setResults([]);
      setVehicleModes({});
      setPickupContacts({});
      setDropContacts({});
      setBulkPickup("");
      setBulkDrop("");
      setRemoved(new Set());
      setSearch("");
      // Use the caller's already-quoted rows when given; otherwise run the bulk-quote ourselves.
      const rows = prequoted ?? (await delhiveryBulkQuote(poNumbers))?.results ?? [];
      if (cancelled) return;
      setResults(rows);
      // Prefill each PO's contacts: default both ends to the assigned ops owner's number, falling
      // back to the quote's own default (option matching its number, else the first option).
      const pickup: Record<string, string> = {};
      const drop: Record<string, string> = {};
      rows.forEach((r) => {
        const assigned = r.assigned_to_contact || "";
        const p = assigned || defaultContactValue(r.pickup_contact_options, r.pickup_contact_number);
        const d = assigned || defaultContactValue(r.drop_contact_options, r.drop_contact_number);
        if (p) pickup[r.po_number] = p;
        if (d) drop[r.po_number] = d;
      });
      setPickupContacts(pickup);
      setDropContacts(drop);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [open, poNumbers, prequoted]);

  // Collapse consolidated rows (same booking_id) into one card — they're a single Delhivery booking
  // for the combined cargo. Non-consolidated rows are their own card. `rep` (first row seen) supplies
  // the shared vehicle/fare/contacts and is the ONE PO we book for the group; `pos` is every PO it
  // covers (for the label); `rows` feeds the combined items list. State is keyed by rep.po_number.
  const cards = React.useMemo(() => {
    const out: BookingCard[] = [];
    const idx = new Map<number, number>();
    results.forEach((r) => {
      if (r.consolidated && r.booking_id != null) {
        const at = idx.get(r.booking_id);
        if (at != null) {
          out[at].rows.push(r);
        } else {
          idx.set(r.booking_id, out.length);
          out.push({
            key: r.po_number,
            rep: r,
            rows: [r],
            pos: r.consolidated_pos ?? [r.po_number],
            consolidated: true,
          });
        }
      } else {
        out.push({ key: r.po_number, rep: r, rows: [r], pos: [r.po_number], consolidated: false });
      }
    });
    return out;
  }, [results]);

  // Crossed-out bookings vanish from the list and from the book payload.
  const visibleCards = cards.filter((c) => !removed.has(c.key));
  const bookableCards = visibleCards.filter((c) => c.rep.bookable);

  // Search narrows only what's rendered (matches any PO in the card) — booking still covers all of
  // bookableCards above, so searching to find one PO never drops the others from the batch.
  const q = search.trim().toLowerCase();
  const displayCards = q
    ? visibleCards.filter((c) => c.pos.some((p) => p.toLowerCase().includes(q)))
    : visibleCards;

  // "Apply to all" — per-card pickers still work afterward (shared state), so individual overrides
  // are possible. Drop is a single shared number; pickup is each booking's OWN vendor contact (they
  // differ per vendor), so instead of a shared dropdown we auto-fill each from its own quote.
  const bulkDropOptions = dedupeContactOptions(
    bookableCards.flatMap((c) => c.rep.drop_contact_options ?? [])
  );
  // Each booking's vendor-contact option (label "Vendor contact") from its own pickup options.
  const vendorContactFor = (c: BookingCard): string | undefined =>
    (c.rep.pickup_contact_options ?? []).find(
      (o: any) => String(o?.label ?? "").toLowerCase() === "vendor contact"
    )?.value;
  const applyVendorContacts = () => {
    // Resolve matches up front — the setState updater runs later (async), so counting inside it
    // would message 0 even when contacts were set.
    const matched = bookableCards
      .map((c) => [c.key, vendorContactFor(c)] as const)
      .filter((e): e is [string, string] => !!e[1]);
    setPickupContacts((prev) => {
      const next = { ...prev };
      matched.forEach(([key, v]) => (next[key] = v));
      return next;
    });
    message.success(
      `Vendor contact set for ${matched.length} booking${matched.length === 1 ? "" : "s"}.`
    );
  };
  // Pickup "apply to all": the Vendor-contact option fills each booking's own vendor contact; any
  // other value is one shared number applied to every booking.
  const onBulkPickupChange = (v: string) => {
    setBulkPickup(v);
    if (v === VENDOR_CONTACT) {
      applyVendorContacts();
      return;
    }
    setPickupContacts((prev) => {
      const next = { ...prev };
      bookableCards.forEach((c) => (next[c.key] = v));
      return next;
    });
  };
  const applyBulkDrop = (v: string) => {
    setBulkDrop(v);
    setDropContacts((prev) => {
      const next = { ...prev };
      bookableCards.forEach((c) => (next[c.key] = v));
      return next;
    });
  };

  const runBulkBook = async () => {
    if (bookableCards.length === 0) return;
    setBooking(true);
    // Per-booking override maps keyed by the representative PO (one PO per consolidated group — its
    // siblings are covered by the same Delhivery order, so we must NOT send them too).
    const vehicle_modes: Record<string, string> = {};
    const pickup_contact_numbers: Record<string, string> = {};
    const drop_contact_numbers: Record<string, string> = {};
    bookableCards.forEach((c) => {
      const po = c.key;
      if (vehicleModes[po]) vehicle_modes[po] = vehicleModes[po];
      const pickup = pickupContacts[po]?.trim();
      const drop = dropContacts[po]?.trim();
      if (pickup) pickup_contact_numbers[po] = pickup;
      if (drop) drop_contact_numbers[po] = drop;
    });
    const res = await delhiveryBulkBook(
      bookableCards.map((c) => c.key),
      { vehicle_modes, pickup_contact_numbers, drop_contact_numbers }
    );
    setBooking(false);
    if (res) {
      Modal.success({
        title: `Booking ${res.count} Delhivery order${res.count === 1 ? "" : "s"}`,
        content:
          res.note ||
          "Booking runs in the background. Track per-PO results in Delhivery Bookings.",
      });
      onBooked?.();
      onClose();
    }
  };

  // Internal quote loading OR the parent re-fetching after a mode change (dispatch-due).
  const isLoading = loading || !!busy;

  return (
    <Modal
      title="Bulk book Delhivery"
      open={open}
      onCancel={onClose}
      width={720}
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button
          key="book"
          type="primary"
          loading={booking}
          disabled={bookableCards.length === 0}
          onClick={runBulkBook}
        >
          Bulk Book ({bookableCards.length})
        </Button>,
      ]}
    >
      <style>{`.delhivery-item-excluded td { background: #fafafa; }`}</style>
      {(note || onModeChange) && (
        <div
          style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12, flexWrap: "wrap" }}
        >
          {note && <Tag color="blue" style={{ margin: 0 }}>{note}</Tag>}
          {onModeChange && (
            <Segmented
              value={mode ?? "all"}
              disabled={isLoading}
              onChange={(v) => onModeChange(v as "all" | "express" | "regular")}
              options={[
                { label: "All", value: "all" },
                { label: "Express", value: "express" },
                { label: "Regular", value: "regular" },
              ]}
            />
          )}
        </div>
      )}
      {isLoading && (
        <div style={{ textAlign: "center", padding: 32 }}>
          <Spin />
          <div style={{ marginTop: 8, color: "#8c8c8c" }}>
            {onModeChange
              ? "Loading dispatch-due POs…"
              : `Fetching quotes for ${poNumbers.length} PO${poNumbers.length === 1 ? "" : "s"}…`}
          </div>
        </div>
      )}

      {!isLoading && visibleCards.length > 0 && (
        <>
          <div style={{ color: "#8c8c8c", marginBottom: 8, fontSize: 13 }}>
            <strong>{bookableCards.length}</strong> of <strong>{visibleCards.length}</strong> bookable
            booking{visibleCards.length === 1 ? "" : "s"}. Delihvery eligible bookings from
            <strong> rebound/horamavu</strong> to the customer filtered by delivery date.
            <strong> Booking runs in the background so expect some delay for it to be visible in Delhivery Bookings </strong>
          </div>

          {/* Apply one pickup + drop contact to every booking at once; per-booking pickers below
              still override. */}
          {bookableCards.length > 1 && (
            <div
              style={{
                padding: "10px 12px",
                marginBottom: 12,
                border: "1px solid #e6f4ff",
                borderRadius: 8,
                background: "#f0f7ff",
              }}
            >
              <Text strong style={{ fontSize: 13 }}>
                Apply to all bookings
              </Text>
              <div style={{ fontSize: 12, color: "#8c8c8c", margin: "2px 0 8px" }}>
                Set one pickup and drop contact for every booking — pick "Vendor contact" to use each
                booking's own vendor. You can still override individual ones afterward.
              </div>
              <Space wrap size={12} align="end">
                <div>
                  <Text type="secondary" style={{ fontSize: 12, display: "block" }}>
                    Pickup contact (all)
                  </Text>
                  <ContactSelect
                    options={[]}
                    extraOptions={PICKUP_EXTRA_OPTIONS}
                    value={bulkPickup}
                    onChange={onBulkPickupChange}
                    width={230}
                  />
                </div>
                <div>
                  <Text type="secondary" style={{ fontSize: 12, display: "block" }}>
                    Drop contact (all)
                  </Text>
                  <ContactSelect
                    options={bulkDropOptions}
                    value={bulkDrop}
                    onChange={applyBulkDrop}
                    width={230}
                  />
                </div>
              </Space>
            </div>
          )}

          <Input.Search
            allowClear
            placeholder="Search PO number"
            style={{ marginBottom: 8 }}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          {displayCards.length === 0 && (
            <div style={{ color: "#8c8c8c", padding: "12px 0", fontSize: 13 }}>
              No bookings match "{search}".
            </div>
          )}

          <div style={{ maxHeight: 460, overflowY: "auto" }}>
            {displayCards.map((c) => {
              const r = c.rep;
              const po = c.key;
              const eligibleVehicles = (r.vehicles ?? []).filter(
                (v: any) => v.eligible !== false
              );
              // Combined cargo across every PO in a consolidated group.
              const items = c.rows.flatMap((row) => row.items ?? []);
              return (
                <div
                  key={po}
                  style={{
                    padding: "10px 12px",
                    marginBottom: 8,
                    border: "1px solid #f0f0f0",
                    borderRadius: 8,
                    background: r.bookable ? "#fff" : "#fafafa",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 12,
                    }}
                  >
                    <div style={{ minWidth: 0 }}>
                      {c.consolidated ? (
                        <Space size={6} wrap>
                          <Text strong>{c.pos.join(", ")}</Text>
                          <Tag color="blue" style={{ marginInlineEnd: 0 }}>
                            one vehicle
                          </Tag>
                        </Space>
                      ) : (
                        <Text strong>{po}</Text>
                      )}
                      {r.mode && (
                        <Tag
                          color={r.mode === "express" ? "orange" : "default"}
                          style={{ marginInlineStart: 8 }}
                        >
                          {r.mode === "express" ? "Express" : "Regular"}
                        </Tag>
                      )}
                      {r.has_escalation && (
                        <Tooltip title={r.escalation ? escalationTooltip(r.escalation) : "Open escalation"}>
                          <Tag color="volcano" style={{ marginInlineStart: 8 }}>
                            ⚠ Escalation
                            {r.escalation?.count && r.escalation.count > 1 ? ` ×${r.escalation.count}` : ""}
                          </Tag>
                        </Tooltip>
                      )}
                      {r.bookable ? (
                        <div style={{ fontSize: 12, color: "#8c8c8c" }}>
                          {r.computed_weight_kg != null
                            ? `${r.computed_weight_kg} kg`
                            : ""}
                          {r.fare != null ? ` · from ${rupee(r.fare)}` : ""}
                        </div>
                      ) : (
                        <div style={{ marginTop: 4 }}>
                          <Tag color="red" style={{ marginInlineEnd: 0 }}>
                            Not bookable
                          </Tag>{" "}
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {cargoRejectionReason(r) || r.error || "Not eligible"}
                          </Text>
                        </div>
                      )}
                      {r.leg_commited_delivery_time && (
                        <div style={{ fontSize: 12, color: "#8c8c8c", marginTop: 4 }}>
                          Committed delivery: {fmtDate(r.leg_commited_delivery_time)}
                        </div>
                      )}
                    </div>

                    <Space size={4}>
                      {r.bookable && (
                        <Select
                          style={{ width: 260 }}
                          placeholder="Auto-pick (recommended)"
                          allowClear
                          value={vehicleModes[po]}
                          onChange={(val) =>
                            setVehicleModes((prev) => {
                              const next = { ...prev };
                              if (val) next[po] = val;
                              else delete next[po];
                              return next;
                            })
                          }
                          options={eligibleVehicles.map((v: any) => ({
                            label: `${v.type} · ${rupee(v.fare?.amount)}${
                              v.priority === 1 ? " (Recommended)" : ""
                            }`,
                            value: v.type,
                          }))}
                        />
                      )}
                      <Tooltip title="Remove from this bulk booking">
                        <Button
                          type="text"
                          size="small"
                          icon={<CloseOutlined />}
                          onClick={() =>
                            setRemoved((prev) => new Set(prev).add(po))
                          }
                        />
                      </Tooltip>
                    </Space>
                  </div>

                  {/* Pickup / drop contact — one shared pair per booking (the group's single truck). */}
                  {r.bookable && (
                    <Space wrap size={12} style={{ marginTop: 10 }}>
                      <div>
                        <Text type="secondary" style={{ fontSize: 12, display: "block" }}>
                          Pickup contact
                        </Text>
                        <ContactSelect
                          options={r.pickup_contact_options}
                          value={pickupContacts[po] ?? ""}
                          onChange={(v) =>
                            setPickupContacts((prev) => ({ ...prev, [po]: v }))
                          }
                          width={230}
                        />
                      </div>
                      <div>
                        <Text type="secondary" style={{ fontSize: 12, display: "block" }}>
                          Drop contact
                        </Text>
                        <ContactSelect
                          options={r.drop_contact_options}
                          value={dropContacts[po] ?? ""}
                          onChange={(v) =>
                            setDropContacts((prev) => ({ ...prev, [po]: v }))
                          }
                          width={230}
                        />
                      </div>
                    </Space>
                  )}

                  {/* Combined item breakdown for every PO in this booking. */}
                  <ItemsMini items={items} />
                </div>
              );
            })}
          </div>
        </>
      )}

      {!isLoading && cards.length === 0 && (
        <Alert type="info" showIcon message="No quotes returned for these POs" />
      )}

      {!isLoading && cards.length > 0 && visibleCards.length === 0 && (
        <Alert
          type="info"
          showIcon
          message="All bookings removed from this batch"
          description="Close and reopen the modal to bring them back."
        />
      )}
    </Modal>
  );
};

export default DelhiveryBulkModal;
